// import React, { useEffect, useState } from "react";
// import { useForgotPasswordMutation } from "../../redux/api/userApi";
// import toast from "react-hot-toast";
// import { useNavigate } from "react-router-dom";
// import { useSelector } from "react-redux";

// const ForgotPassword = () => {
//   const [email, setEmail] = useState("");
//   const navigate = useNavigate();
//   const [forgotPassword, { isLoading, error, isSuccess }] = useForgotPasswordMutation();

//   const { isAuthenticated } = useSelector((state) => state.auth);

//   useEffect(() => {
//     if (isAuthenticated) {
//       navigate("/");
//     }
//     if (error) {
//       toast.error(error?.data?.message);
//     }
//     if(isSuccess){
//         toast.success("Email sent. Please check your inbox")
//     }
//   }, [error, isAuthenticated, isSuccess, navigate]);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
    
//     forgotPassword({email})
//   };

//   return (
//     <section className="max-w-md mx-auto my-20 px-6 py-10 bg-[#f5f5dc] shadow-md rounded-md">
//       <h2 className="text-2xl font-playfair text-amber-700 mb-4 text-center">
//         Forgot Password
//       </h2>
//       <p className="text-sm font-sourcesans text-stone-600 text-center mb-6">
//         Enter the email associated with your account. You’ll receive a link to reset your password.
//       </p>

//       <form onSubmit={handleSubmit} className="space-y-6">
//         {/* Email Input */}
//         <div>
//           <label
//             htmlFor="email"
//             className="block text-sm font-sourcesans text-stone-700 mb-1"
//           >
//             Email Address
//           </label>
//           <input
//             type="email"
//             id="email"
//             required
//             value={email}
//             onChange={(e) => setEmail(e.target.value)}
//             className="w-full px-4 py-2 border border-stone-300 bg-white text-stone-800 placeholder-stone-500 font-sourcesans rounded-md focus:outline-none focus:ring-amber-600 focus:border-amber-600"
//             placeholder="you@example.com"
//           />
//         </div>

//         {/* Submit Button */}
//         <div>
//           <button
//             type="submit"
//             disabled={isLoading}
//             className="w-full bg-amber-700 hover:bg-amber-800 text-white px-6 py-2 rounded-md text-sm font-sourcesans focus:outline-none focus:ring-2 focus:ring-amber-600"
//           >
//             {isLoading ? "Sending..." : "Send Reset Link"}
//           </button>
//         </div>
//       </form>
//     </section>
//   );
// };

// export default ForgotPassword;

import React, { useEffect, useRef, useState } from "react";
import { useForgotPasswordMutation } from "../../redux/api/userApi";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";

const useParticles = (canvasRef) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId, frame = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener("resize", resize);
    const particles = Array.from({ length: 38 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.3,
      speed: Math.random() * 0.00015 + 0.00005,
      opacity: Math.random() * 0.18 + 0.04,
      phase: Math.random() * Math.PI * 2,
    }));
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      particles.forEach((p) => {
        const x = ((p.x + frame * p.speed) % 1) * canvas.width;
        const y = p.y * canvas.height;
        const pulse = Math.sin(frame * 0.012 + p.phase) * 0.5 + 0.5;
        ctx.beginPath();
        ctx.arc(x, y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(188, 63, 74, ${p.opacity * pulse})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, [canvasRef]);
};

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  useParticles(canvasRef);

  const [forgotPassword, { isLoading, error, isSuccess }] = useForgotPasswordMutation();
  const { isAuthenticated } = useSelector((state) => state.auth);

  useEffect(() => {
    if (isAuthenticated) navigate("/");
    if (error) toast.error(error?.data?.message);
    if (isSuccess) toast.success("Email sent. Please check your inbox.");
  }, [error, isAuthenticated, isSuccess, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    forgotPassword({ email });
  };

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <>
      <Helmet>
        <title>Forgot Password — Sanguine Accent</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="relative min-h-screen bg-[#f5f3ea] flex items-center justify-center px-4 overflow-hidden">

        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none" aria-hidden="true" />

        <motion.div
          className="relative z-10 max-w-md w-full space-y-6 bg-white/70 backdrop-blur-sm p-8 rounded-lg shadow-sm"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <div className="text-center space-y-1">
            <p className="text-[10px] uppercase tracking-[0.25em] text-stone-400 font-sourcesans">Account</p>
            <h1 className="text-3xl font-playfair font-bold text-[#BC3F4A]">Forgot Password</h1>
          </div>

          <div className="w-12 h-px bg-[#BC3F4A]/40 mx-auto" />

          <p className="text-sm font-sourcesans text-stone-500 text-center leading-relaxed">
            Enter the email associated with your account. You'll receive a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <input
              type="email"
              id="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="w-full px-4 py-3 border border-[#e2e1d8] bg-white text-[#1c1917] placeholder-stone-400 font-sourcesans rounded-md focus:outline-none focus:border-[#BC3F4A] transition text-sm"
            />

            {error && (
              <p className="text-red-600 text-sm font-sourcesans">{error.data?.message}</p>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-4 bg-[#BC3F4A] hover:bg-[#a3323b] text-white font-playfair rounded-md transition duration-300 disabled:opacity-50"
            >
              {isLoading ? "Sending..." : "Send Reset Link"}
            </button>
          </form>

          <p className="text-center text-[10px] uppercase tracking-[0.2em] text-stone-400 font-sourcesans pt-2">
            Make it. Mean it.
          </p>
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#BC3F4A]/40 to-transparent" />
      </div>
    </>
  );
};

export default ForgotPassword;